import React from "react";
import Image from "next/image";
import { type EventsPage } from "lib/sanity";
import { Flex, Link, List, Text, Box, AspectRatio, ListItem } from "@chakra-ui/react";
import ReactMarkdown from "react-markdown";
import { Page } from "components/page";
import { EventHero } from "components/events/event-hero";
import { Section } from "components/section";
import { eventNewsletterCta } from "components/events-newsletter-form";
import { EventPartners } from "./event-partners";
import { EventSpeakers } from "./event-speakers";

const getShopTalkAdditionalFields = (
  hubSpotFormAdditionalFields: EventsPage["hubSpotFormAdditionalFields"],
): EventsPage["hubSpotFormAdditionalFields"] => {
  return [
    ...(hubSpotFormAdditionalFields ?? []),
    {
      required: true,
      fieldName: "transportation",
      fieldLabel: "Transportation options",
      fieldType: "select",
      selectOptions: [
        { label: "I'll take a shuttle from the Mandalay", value: "shuttle" },
        { label: "I can get there on my own", value: "own" },
      ],
    },
  ];
};

function EventPageTemplate({
  hubSpotFormAdditionalFields,
  imageUrl,
  partners,
  speakers,
  about,
  address,
  mapEmbedUrl,
  metaTitle,
  metaDescription,
  metaImageUrl,
  ...props
}: EventsPage) {
  const isShopTalkEvent = props.slug === "shoptalk-happy-hour";
  const shopTalkAdditionalFields = getShopTalkAdditionalFields(hubSpotFormAdditionalFields);

  return (
    <Page
      title={metaTitle}
      description={
        metaDescription ??
        "Hightouch helps you easily and efficiently sync data from your warehouse to your SaaS tools without scripts, CSVs or a CDP."
      }
      image={{
        width: 800,
        height: 418,
        url: metaImageUrl ?? imageUrl,
        alt: metaTitle,
      }}
      footerOptions={{
        ctaProps: eventNewsletterCta,
      }}
    >
      <EventHero
        hubSpotFormAdditionalFields={isShopTalkEvent ? shopTalkAdditionalFields : hubSpotFormAdditionalFields}
        {...props}
      />

      {partners && partners.length > 0 && <EventPartners partners={partners} />}

      {speakers && speakers.length > 0 && <EventSpeakers speakers={speakers} />}

      <Section>
        <Flex align="center" direction={{ base: "column", lg: "row" }} gap={{ base: 8, lg: 16, xl: 24 }}>
          <Box flex={1}>
            <ReactMarkdown
              components={{
                a: ({ node, href, ...props }) => <Link href={href} variant="inlineDark" {...props} />,
                h2: ({ node, ...props }) => <Text as="h2" textStyle="headline1" mb="smaller" {...props} />,
                h3: ({ node, ...props }) => <Text as="h3" textStyle="headline2" mt={8} mb={4} {...props} />,
                p: ({ node, ...props }) => <Text as="p" textStyle="article.p" {...props} />,
                li: ({ children }) => <ListItem textStyle="article.listItem">{children}</ListItem>,
                ol: ({ children }) => <List textStyle="article.list">{children}</List>,
                ul: ({ children }) => (
                  <List textStyle="article.list" listStyleType="disc">
                    {children}
                  </List>
                ),
              }}
            >
              {about}
            </ReactMarkdown>
          </Box>

          <Box flex={1} w="100%">
            <AspectRatio
              pos="relative"
              ratio={2 / 1}
              w="100%"
              maxW={{ base: "560px", lg: "100%" }}
              borderRadius="base"
              overflow="hidden"
            >
              <Image
                fill
                src={imageUrl + "?w=1600"}
                alt={`${props.title}.`}
                style={{
                  objectFit: "cover",
                }}
              />
            </AspectRatio>
          </Box>
        </Flex>
      </Section>

      {address && mapEmbedUrl && (
        <Section>
          <Flex direction="column" align="start" gap={4}>
            <Text as="h2" textStyle="headline1">
              Venue address
            </Text>

            <Text textStyle="article.p" mb="0">
              {address}
            </Text>

            {mapEmbedUrl && (
              <Box borderRadius="base" overflow="hidden" width="100%" height="450px" mt={6}>
                <iframe
                  width="100%"
                  height="450"
                  loading="lazy"
                  src={`${mapEmbedUrl}&key=AIzaSyCyRZ1UWbPQSfCAok0-dCwbjIt3TP97Tak`}
                />
              </Box>
            )}
          </Flex>
        </Section>
      )}
    </Page>
  );
}

export default EventPageTemplate;
